/**
 * Cart Debug Utility
 * Logs cart state changes, quantity updates, and DOM sync issues
 */

(function() {
  'use strict';

  const CartDebug = {
    enabled: true,
    logs: [],

    log(...args) {
      if (!this.enabled) return;
      const timestamp = new Date().toISOString();
      const message = `[${timestamp}] ${args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : a).join(' ')}`;
      console.log(message);
      this.logs.push(message);
    },

    error(...args) {
      if (!this.enabled) return;
      const timestamp = new Date().toISOString();
      const message = `[${timestamp}] ERROR: ${args.map(a => typeof a === 'object' ? JSON.stringify(a, null, 2) : a).join(' ')}`;
      console.error(message);
      this.logs.push(message);
    },

    /**
     * Monitor cart-icon-bubble updates
     */
    monitorCartIcon() {
      const cartIcon = document.getElementById('cart-icon-bubble');
      if (!cartIcon) {
        this.error('cart-icon-bubble not found');
        return;
      }

      this.log('Monitoring cart-icon-bubble:', cartIcon.innerHTML);

      // Watch for changes
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          const badge = cartIcon.querySelector('.cart-count-bubble');
          const count = badge ? badge.textContent : '(empty)';
          this.log('cart-icon-bubble updated. Count:', count);
          this.log('Full HTML:', cartIcon.innerHTML);
        });
      });

      observer.observe(cartIcon, { childList: true, subtree: true, characterData: true });
      this.log('Watching cart-icon-bubble for changes');
    },

    /**
     * Monitor quantity inputs in cart drawer
     */
    monitorQuantityInputs() {
      const observer = new MutationObserver(() => {
        const quantities = document.querySelectorAll('[id^="Drawer-quantity-"]');
        this.log(`Found ${quantities.length} quantity inputs in cart drawer`);
        quantities.forEach((input) => {
          this.log(`  ${input.id}: value=${input.value}, data-value=${input.getAttribute('value')}`);
        });
      });

      const cartDrawer = document.querySelector('cart-drawer-items');
      if (cartDrawer) {
        observer.observe(cartDrawer, { childList: true, subtree: true });
        this.log('Watching cart-drawer-items for quantity input changes');
      } else {
        this.error('cart-drawer-items not found');
      }
    },

    /**
     * Monitor cart total updates
     */
    monitorCartTotal() {
      const observer = new MutationObserver(() => {
        const totalElement = document.querySelector('.totals__total-value');
        if (totalElement) {
          this.log('Cart total updated:', totalElement.textContent);
        }
      });

      const footer = document.querySelector('.cart-drawer__footer');
      if (footer) {
        observer.observe(footer, { childList: true, subtree: true, characterData: true });
        this.log('Watching cart footer for total changes');
      }
    },

    /**
     * Hook into cart update events
     */
    monitorCartUpdates() {
      const originalPublish = window.publish;
      if (originalPublish) {
        window.publish = function(...args) {
          if (args[0] === 'cart:update' || args[0]?.includes?.('cartUpdate')) {
            CartDebug.log('Cart update event published:', args);
          }
          return originalPublish.apply(window, args);
        };
        this.log('Hooked into publish() for cart:update events');
      }
    },

    /**
     * Compare cart state across sources
     */
    compareCartState() {
      const state = {
        cartIcon: {
          badge: null,
          count: null,
        },
        cartDrawer: {
          itemCount: 0,
          quantities: [],
          total: null,
        },
        timestamp: new Date().toISOString(),
      };

      // Check cart icon
      const badge = document.querySelector('.cart-count-bubble');
      if (badge) {
        state.cartIcon.badge = badge.innerHTML;
        state.cartIcon.count = parseInt(badge.textContent) || 0;
      }

      // Check cart drawer
      const quantities = document.querySelectorAll('[id^="Drawer-quantity-"]');
      state.cartDrawer.itemCount = quantities.length;
      quantities.forEach((input) => {
        state.cartDrawer.quantities.push({
          id: input.id,
          value: input.value,
          'data-value': input.getAttribute('value'),
        });
      });

      const totalEl = document.querySelector('.totals__total-value');
      if (totalEl) {
        state.cartDrawer.total = totalEl.textContent;
      }

      return state;
    },

    /**
     * Validate cart state consistency
     */
    validateState() {
      const state = this.compareCartState();
      const issues = [];

      // Check if icon badge matches drawer item count
      if (state.cartIcon.count !== state.cartDrawer.itemCount) {
        issues.push(
          `Icon badge (${state.cartIcon.count}) does not match drawer items (${state.cartDrawer.itemCount})`
        );
      }

      // Check if quantity values match their data attributes
      state.cartDrawer.quantities.forEach((qty) => {
        if (qty.value !== qty['data-value']) {
          issues.push(
            `Quantity mismatch in ${qty.id}: value="${qty.value}" vs data-value="${qty['data-value']}"`
          );
        }
      });

      if (issues.length === 0) {
        this.log('✓ Cart state is consistent');
      } else {
        this.error('Cart state inconsistencies found:');
        issues.forEach((issue) => this.error(`  - ${issue}`));
      }

      this.log('Current state:', state);
      return { state, issues };
    },

    /**
     * Initialize all monitoring
     */
    init() {
      this.log('='.repeat(60));
      this.log('Cart Debug initialized');
      this.log('='.repeat(60));

      // Wait for DOM to be ready
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
          this.monitorCartIcon();
          this.monitorQuantityInputs();
          this.monitorCartTotal();
          this.monitorCartUpdates();
          this.validateState();
        });
      } else {
        this.monitorCartIcon();
        this.monitorQuantityInputs();
        this.monitorCartTotal();
        this.monitorCartUpdates();
        this.validateState();
      }
    },

    /**
     * Export logs to console or file
     */
    exportLogs() {
      console.log('='.repeat(60));
      console.log('CART DEBUG LOGS');
      console.log('='.repeat(60));
      this.logs.forEach((log) => console.log(log));
      console.log('='.repeat(60));
      return this.logs.join('\n');
    },
  };

  // Make available globally
  window.CartDebug = CartDebug;

  // Auto-initialize
  CartDebug.init();
})();
